import {Injectable} from "@angular/core";
import {CCRoles} from "../../../model/security/cc-roles-type";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {FormFieldGroupConfig} from "../../../model/api/form-field-group-config";
import {CustomerAccountDataLoadingService} from "./customer-account-data-loading.service";
import {AccountType} from "../../../model/exchange/account-type";
import {AccountComponent} from "../../../crystal-clear/customer/account/account.component";
import {AppConfigService} from "../../app-config.service";
import TYPE = FormFieldConfig.TYPE;

@Injectable({
  providedIn: 'root'
})
export class CustomerAccountFormConfigService {

  constructor(readonly appConfigService: AppConfigService,
              private dataLoadingService: CustomerAccountDataLoadingService) {
  }

  public loadConfig(component: AccountComponent): FormFieldGroupConfig[] {

    const groupCustExhFields = [
      {
        field: 'owner',
        optionValue: 'text',
        required: true,
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.dataLoadingService.owners,
        changed: event => {
          this.dataLoadingService.filterCustomers(component.formRecord);
          this.dataLoadingService.refreshBrokerageTables(component.formRecord);
          this.dataLoadingService.refreshMoneyOwners(component.formRecord);
        }
      },
      {
        field: 'customerId',
        required: true,
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.dataLoadingService.displayedCustomers,
      },
      {
        type: TYPE.NONE,
        column: 6,
      },
      {
        field: 'clearingHouse',
        required: true,
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.dataLoadingService.clearingHouses,
        changed: event => {
          this.dataLoadingService.filterAccountType(component.formRecord);
          this.dataLoadingService.filterExchanges(component.formRecord);
        }
      },
      {
        field: 'exchangeCode',
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        icon: (id: string) => {
          if (this.dataLoadingService.exchangeCodes) {
            const exchField = this.dataLoadingService.exchangeCodes.find(exchange => exchange.value && exchange.value.code === id);
            if (exchField && exchField.value)
              return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
          }
          return 'fas fa-globe-europe';
        },
        options: () => this.dataLoadingService.displayedExchangeCodes,
        changed: event => {
          this.dataLoadingService.filterAccountType(component.formRecord);
        }
      },
      {
        field: 'enabled',
        column: 2,
        type: TYPE.CHECKBOX
      }];
    if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      groupCustExhFields.push(
        {
          field: 'waitForApproval',
          column: 4,
          type: TYPE.CHECKBOX
        },
        {
          field: 'allFutures',
          column: 3,
          type: TYPE.CHECKBOX
        },
        {
          field: 'allOptions',
          column: 3,
          type: TYPE.CHECKBOX
        },
        {
          type: TYPE.NONE,
          column: 6,
        },
        {
          field: 'contractList',
          column: 8,
          type: TYPE.TEXT
        },
        {
          field: 'contractListInclude',
          column: 4,
          type: TYPE.CHECKBOX
        });
    }

    const groupDetailsFields: FormFieldConfig[] = [{
      field: 'code',
      required: true,
      column: 2,
      type: TYPE.TEXT,
      focused: true
    },
      {
        field: 'accountTypeCode',
        required: true,
        filter: true,
        column: 1,
        type: TYPE.AUTOCOMPLETE,
        options: () => this.dataLoadingService.displayedAccountTypes,
      },
      {
        field: 'traderName',
        column: 2,
        filter: true,
        type: TYPE.AUTOCOMPLETE,
        options: () => component.getDistinctOpts(this.dataLoadingService.traders),
        changed: event => {
          component.formRecord.traderName = event.text;
        }
      }];
    if (this.appConfigService.iceTraderMemoFeature) {
      groupDetailsFields.push(
        {
          field: 'bindTraderToMemo',
          column: 2,
          type: TYPE.CHECKBOX,

        })
    }
    if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      groupDetailsFields.push(
        {
          field: 'executionFee',
          column: 1,
          type: TYPE.TEXT
        },
        {
          field: 'moneyOwner',
          column: 2,
          type: TYPE.DROPDOWN,
          options: () => this.dataLoadingService.moneyOwnersVisibles
        },
        {
          field: 'agreementId',
          column: 2,
          type: TYPE.DROPDOWN,
          options: () => this.dataLoadingService.brokerageTablesVisibles
        }
      );
    }
    const groupGiveupFields: FormFieldConfig[] = [
      {
        field: 'giveUpBrokerCode',
        required: true,
        column: 2,
        type: TYPE.TEXT
      },
      {
        field: 'giveUpMessage',
        required: true,
        column: 2,
        type: TYPE.TEXT
      },
      {
        field: 'giveupAccountType',
        filter: true,
        column: 2,
        type: TYPE.AUTOCOMPLETE,
        options: () => this.dataLoadingService.displayedGUpAccountTypes,
      },
      {
        field: 'giveupMessage2',
        column: 2,
        type: TYPE.TEXT
      },
      {
        field: 'giveupMessage3',
        column: 2,
        type: TYPE.TEXT
      }
    ];
    if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      groupGiveupFields.push(
        {
          field: 'giveUpCommission',
          column: 2,
          type: TYPE.TEXT
        }
      );
    }
    const formConfig = [
      {
        group: 'customer_exchange',
        fields: groupCustExhFields
      },
      {
        group: 'details',
        fields: groupDetailsFields
      },
      {
        group: 'clearing',
        hidden: record => AccountType.GIVE_UP_CODE === record.accountTypeCode,
        fields: [
          {
            field: 'reference1',
            required: true,
            column: 2,
            type: TYPE.TEXT
          },
          {
            field: 'reference2',
            column: 2,
            type: TYPE.TEXT
          },
          {
            field: 'reference3',
            column: 2,
            type: TYPE.TEXT,
          },
          {
            field: 'openCloseForFutures',
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.dataLoadingService.openCloses,
          },
          {
            field: 'openCloseForOptions',
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.dataLoadingService.openCloses,
          },
        ]
      },
      {
        group: 'giveup',
        hidden: record => AccountType.GIVE_UP_CODE != record.accountTypeCode,
        fields: groupGiveupFields
      }

    ];

    return formConfig;
  }

}
